import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';

import {
  CheckCircle,
  CircleNotch,
  FloppyDisk,
  Info,
  Shield,
  WarningCircle
} from '@phosphor-icons/react';
import { WorkspaceHeader, WorkspaceContent } from '../components/WorkspaceLayout';
const VoiceSettings = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    voice_enabled: false,
    voice_enabled_agents_limit: 1,
    max_clip_duration_seconds: 60,
    allow_external_calls: false,
    allow_meeting_bots: false,
    require_consent_prompt: true,
    consent_message: "This call may be recorded for quality assurance purposes."
  });
  const [userPlan, setUserPlan] = useState('pro');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
      fetchSettings();
    }
  }, [activeOrganization]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/org/settings/voice?organization_id=${activeOrganization.organization_id}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Merge with defaults to ensure all fields exist
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
        setUserPlan(data.user_plan || 'pro');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const url = `${BACKEND_URL}/api/org/settings/voice?organization_id=${activeOrganization.organization_id}`;
      
      // Map frontend field names to backend field names
      const payload = {
        voice_enabled: settings.voice_enabled,
        voice_max_clip_seconds: settings.max_clip_duration_seconds,
        voice_enabled_agents_limit: settings.voice_enabled_agents_limit
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Voice controls saved successfully!' });
        toast.success('Voice controls saved successfully!', {
          duration: 3000,
        });
        setTimeout(() => setMessage(null), 5000);
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors which come as an array
        let errorMessage = 'Failed to save settings. Please try again.';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation error format
            errorMessage = errorData.detail.map(err => err.msg || err.message).join(', ');
          }
        }
        setMessage({ type: 'error', text: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = 'An error occurred while saving settings.';
      setMessage({ type: 'error', text: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => {
      if (!prev) return prev;
      return { ...prev, [field]: !prev[field] };
    });
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const getMaxAgentsDisplay = () => {
    if (userPlan === 'partner_pro') {
      return settings?.voice_enabled_agents_limit === -1 ? 'Unlimited' : settings?.voice_enabled_agents_limit || 0;
    }
    return '1 (Pro Plan)';
  };

  const isPartnerPro = userPlan === 'partner_pro';

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-0)]">
      <WorkspaceHeader
        icon={Shield}
        title="Voice Controls"
        description="Organization-level security, enablement, and safety limits for voice-enabled AI agents"
        badge={
          <span className="px-2 py-0.5 rounded-md bg-[var(--surface-3)] text-[var(--text-muted)] text-[10px] font-semibold">
            {isPartnerPro ? 'Partner Pro' : 'Pro'}
          </span>
        }
      />

      <WorkspaceContent>
        <div className="max-w-3xl">

      {/* Message Banner */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`glass rounded-2xl p-4 mb-6 border-2 ${
            message.type === 'success'
              ? 'border-[#14F195]/30 bg-[#14F195]/10'
              : 'border-[#EF4444]/30 bg-[#EF4444]/10'
          }`}
        >
          <div className="flex items-center gap-3">
            {message.type === 'success' ? (
              <CheckCircle size={24} className="text-[#14F195]" />
            ) : (
              <WarningCircle size={24} className="text-[#EF4444]" />
            )}
            <p className={message.type === 'success' ? 'text-[#14F195]' : 'text-[#EF4444]'}>
              {message.text}
            </p>
          </div>
        </motion.div>
      )}

      {/* Settings Card */}
      <div className="glass rounded-3xl p-8">
        {/* 1. Enablement - Master Toggle */}
        <div className="mb-8 pb-8 border-b border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Enable Voice for Organization</h3>
              <p className="text-[#A0AEC0] text-sm">
                Master switch to enable or disable voice features across all agents in this organization.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.voice_enabled || false}
                onChange={() => handleToggle('voice_enabled')}
                className="sr-only peer"
              />
              <div className="w-14 h-8 bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] rounded-full peer-checked:bg-gradient-to-r peer-checked:from-[#6366F1] peer-checked:to-[#EC4899] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform peer-checked:translate-x-6"></div>
            </label>
          </div>
        </div>

        {/* 2. Safety & Governance Limits */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-white mb-6">Safety & Governance Limits</h3>
          
          <div className="space-y-6">
            {/* Max Voice-Enabled Agents */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Max Voice-Enabled Agents
              </label>
              <p className="text-[#A0AEC0] text-sm mb-3">
                Maximum number of agents that may have voice enabled.
              </p>
              {isPartnerPro ? (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="-1"
                    value={settings?.voice_enabled_agents_limit || -1}
                    onChange={(e) => handleChange('voice_enabled_agents_limit', parseInt(e.target.value))}
                    className="flex-1 px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
                  />
                  <span className="text-[#A0AEC0] text-sm whitespace-nowrap">(-1 = unlimited)</span>
                </div>
              ) : (
                <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)]">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">1 voice agent</span>
                    <span className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-3 py-1 rounded-full">
                      Pro Plan Limit
                    </span>
                  </div>
                  <p className="text-[#A0AEC0] text-xs mt-2">
                    Upgrade to Partner Pro for unlimited voice-enabled agents
                  </p>
                </div>
              )}
            </div>

            {/* Max Clip Duration */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Max Clip Duration (seconds)
              </label>
              <p className="text-[#A0AEC0] text-sm mb-3">
                Maximum length of a single voice recording to protect system stability.
              </p>
              <input
                type="number"
                min="5"
                max="300"
                value={settings?.voice_max_clip_seconds || 60}
                onChange={(e) => handleChange('voice_max_clip_seconds', parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
              />
              <p className="text-[#718096] text-xs mt-2">Default: 60 seconds (Range: 5-300)</p>
            </div>
          </div>
        </div>

        {/* 3. Responsibility Disclosure */}
        <div className="mb-8 pb-8 border-t border-[rgba(255,255,255,0.1)] pt-8">
          <div className="glass rounded-2xl p-6 border-2 border-[#6366F1]/30 bg-[#6366F1]/5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#6366F1]/20 flex items-center justify-center flex-shrink-0">
                <Info size={20} className="text-[#6366F1]" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Voice Billing & Responsibility</h4>
                <div className="text-[#A0AEC0] text-sm space-y-2">
                  <p>
                    Man & Machine does not meter or bill voice usage.
                  </p>
                  <p>
                    All voice processing costs are charged directly by your selected AI provider using your own API keys.
                  </p>
                  <p>
                    Usage limits, quotas, and billing controls are managed by your provider.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FloppyDisk Button */}
        <div className="pt-6 border-t border-[rgba(255,255,255,0.1)]">
          <button
            onClick={handleSave}
            disabled={saving}
            className="relative overflow-hidden rounded-full group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
            <span className="relative z-10 flex items-center justify-center gap-2 px-8 py-3 text-white font-bold">
              {saving ? (
                <>
                  <CircleNotch size={20} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk size={20} />
                  Save Voice Controls
                </>
              )}
            </span>
          </button>
        </div>
      </div>
      </div>
      </WorkspaceContent>
    </div>
  );
};

export default VoiceSettings;
